import React, { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/useAuthUser'
import { useSourcesQuery, isEmail, isArticle, type SourceType } from '../../requests/sources'
import { getInbox } from '../../requests/inboxes'
import { Page, Section } from '../../components/Page'
import { Skeleton } from '../../components/Skeleton'
import { Empty } from '../../components/Empty'
import './styles.css'

export const ViewFolderSources: React.FC = () => {
  const { inboxId } = useParams<{ inboxId: string }>()
  const { authUser } = useAuth()


  // Calculate past 2 weeks
  const twoWeeksAgo = useMemo(() => {
    const now = new Date()
    const date = new Date(now)
    date.setDate(date.getDate() - 14)
    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const {
    data: inbox,
    isLoading: loadingInbox
  } = useQuery({
    queryKey: ['inbox', inboxId],
    queryFn: async () => {
      const res = await getInbox(inboxId!)
      return res.data
    },
    enabled: !!inboxId && authUser?.superAdmin
  })

  const {
    data: sources = [],
    isLoading: loadingSources
  } = useSourcesQuery(
    inboxId || '',
    {
      startDate: twoWeeksAgo
    },
    {
      enabled: !!inboxId && authUser?.superAdmin
    }
  )

  // Super admin check
  if (!authUser?.superAdmin) {
    return null
  }

  const loading = loadingInbox || loadingSources

  const getTitle = (source: SourceType): string => {
    if (isEmail(source)) {
      return source.subject
    }
    if (isArticle(source)) {
      return source.title
    }
    return 'Unknown'
  }

  const getType = (source: SourceType): string => {
    if (isEmail(source)) {
      return 'Email'
    }
    if (isArticle(source)) {
      return 'Article'
    }
    return 'Unknown'
  }

  const getPublication = (source: SourceType): string => {
    if (isEmail(source)) {
      return source.newsletterName || "can't determine"
    }
    if (isArticle(source)) {
      return source.source || "can't determine"
    }
    return "can't determine"
  }

  const getActionLink = (source: SourceType): { href: string; label: string; isExternal?: boolean } => {
    if (isEmail(source)) {
      return { 
        href: `/app/folders/${inboxId}/emails/${source._id}`, 
        label: 'View Email Content',
        isExternal: false
      }
    }
    if (isArticle(source)) {
      return { 
        href: source.link, 
        label: 'View Article',
        isExternal: true
      }
    }
    return { href: '#', label: 'View', isExternal: false }
  }

  const getEmailIgnored = (source: SourceType): string => {
    if (isEmail(source)) {
      return source.ignore ? 'Yes' : 'No'
    }
    return ''
  }

  const sectionTitle = inbox 
    ? `Last Two Weeks of Sources - ${inbox.name}`
    : 'Last Two Weeks of Sources'

  if (loading) {
    return (
      <Page>
        <Section title={sectionTitle}>
          <table className="sources-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Publication</th>
                <th>Email Ignored</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td><Skeleton width={200} /></td>
                  <td><Skeleton width={60} /></td>
                  <td><Skeleton width={120} /></td>
                  <td><Skeleton width={40} /></td>
                  <td><Skeleton width={100} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </Page>
    )
  }

  if (sources.length === 0) {
    return (
      <Page>
        <Section title={sectionTitle}>
          <Empty>No sources found for the past 2 weeks.</Empty>
        </Section>
      </Page>
    )
  }

  return (
    <Page>
      <Section title={sectionTitle}>
        <table className="sources-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Publication</th>
              <th>Email Ignored</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const action = getActionLink(source)
              return (
                <tr key={source._id}>
                  <td>{getTitle(source)}</td>
                  <td>{getType(source)}</td>
                  <td>{getPublication(source)}</td>
                  <td>{getEmailIgnored(source)}</td>
                  <td>
                    {action.isExternal ? (
                      <a href={action.href} target="_blank" rel="noopener noreferrer">
                        {action.label}
                      </a>
                    ) : (
                      <Link to={action.href}>
                        {action.label}
                      </Link>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Section>
    </Page>
  )
}
